import React, { type PropsWithChildren } from "react";
import { SimpleGrid } from "@chakra-ui/react";
import { Section, sectionSpacingY } from "components/section";
import { TextColumn, TextTitle } from "ui/text";
import {
  CodeIcon,
  ExperimentsIcon,
  HeartIcon,
  LeversIcon,
  MegaphoneIcon,
  MinusCircleIcon,
  PersonIcon,
  SparklesIcon,
  TrendIcon,
} from "ui/icons";
import { FilterTabs } from "ui/tabs";
import { Rule } from "ui/rule";

type TabSectionProps = {
  isExtended?: boolean;
};

function TabSection({ isExtended, children }: PropsWithChildren<TabSectionProps>) {
  return (
    <SimpleGrid
      columns={{ base: 1, md: isExtended ? 2 : 3, lg: isExtended ? 4 : 3 }}
      gap={{ base: "biggest", md: "small" }}
    >
      {children}
    </SimpleGrid>
  );
}

function FirstTabSection({ isExtended }: TabSectionProps) {
  return (
    <TabSection isExtended={isExtended}>
      <TextColumn
        iconChip={{ variant: "red", icon: HeartIcon }}
        title="Retain users with data-driven loyalty programs"
        description="Use data to create loyalty programs that work. Power the best offers and most relevant messages to every customer."
        cta={{ href: "/customers/petsmart" }}
      />
      <TextColumn
        iconChip={{ variant: "yellow", icon: TrendIcon }}
        title="Increase LTV with 1-1 personalized product offers"
        description="Always recommend the next-best-action by leveraging all of your organization’s data including data science and product catalogs."
        cta={{ href: "/customers/wmg" }}
      />
      {isExtended && (
        <TextColumn
          iconChip={{ variant: "blue", icon: MegaphoneIcon }}
          title="Drive more revenue with high-converting campaigns"
          description="Unlock highly personalized triggered campaigns like 1-1 abandon cart emails, reminders, and product recommendations."
          cta={{ href: "/playbooks/abandon-cart-emails" }}
        />
      )}
      <TextColumn
        iconChip={{ variant: "violet", icon: PersonIcon }}
        title="Drive higher engagement with omni-channel personalization"
        description="Make sure customers are receiving the right message on the right channel at the right time by centrally managing your audience logic."
        cta={{ href: "/playbooks/product-utilization-emails-iterable" }}
      />
    </TabSection>
  );
}

function SecondTabSection({ isExtended }: TabSectionProps) {
  return (
    <TabSection isExtended={isExtended}>
      <TextColumn
        iconChip={{ variant: "violet", icon: MinusCircleIcon }}
        title="Reduce wasted spend with better suppression"
        description="Build custom suppression audiences and actively sync them into advertising platforms to optimize performance."
        cta={{ href: "/playbooks/Suppress-Existing-Customers-from-Facebook-Campaigns" }}
      />
      <TextColumn
        iconChip={{ variant: "green", icon: CodeIcon }}
        title="Increase ROAS by switching from pixels to conversion APIs"
        description="Enrich conversion events with first-party data through conversion APIs for higher user match rates and increased targeting accuracy."
        cta={{ href: "/playbooks/send-better-signals-with-facebook-conversion-api" }}
      />
      <TextColumn
        iconChip={{ variant: "yellow", icon: MegaphoneIcon }}
        title="Re-engage churned customers with hyper-targeted remarketing"
        description="Unlock highly personalized triggered campaigns like 1-1 abandon cart emails, reminders, and product recommendations."
        cta={{ href: "/playbooks/improve-conversions-with-personalized-funnel-based-ad-retargeting" }}
      />
      {isExtended && (
        <TextColumn
          iconChip={{ variant: "red", icon: TrendIcon }}
          title="Increase revenue driven from ads by increasing reach"
          description="Make sure customers are receiving the right message on the right channel at the right time by centrally managing your audience logic."
          cta={{ href: "/solutions/advertising" }}
        />
      )}
    </TabSection>
  );
}

function ThirdTabSection({ isExtended }: TabSectionProps) {
  return (
    <TabSection isExtended={isExtended}>
      <TextColumn
        iconChip={{ variant: "blue", icon: LeversIcon }}
        title="Understand the true lift of every channel with holdout testing"
        description="Understand incrementally of email and advertising programs with audience level hold-out testing"
        cta={{ href: "/platform/customer-studio#splits" }}
      />
      <TextColumn
        iconChip={{ variant: "yellow", icon: ExperimentsIcon }}
        title="Drive higher ROI with omni-channel experimentation"
        description="Unlock audience-level testing across multiple channels. Simply measure user-level ROI across multiple treatments."
        cta={{ href: "/docs/customer-studio/splits", target: "_blank" }}
      />
      <TextColumn
        iconChip={{ variant: "violet", icon: SparklesIcon }}
        title="Automate experimentation with AI Decisioning"
        description="Move away from manual testing and one-off insights. Power 1-1 experimentation at a user level with AI."
        cta={{ href: "/platform/ai-decisioning" }}
      />
      {isExtended && (
        <TextColumn
          iconChip={{ variant: "green", icon: MegaphoneIcon }}
          title="Move more confidently with complete campaign data"
          description="Understand the results of tests direction in Hightouch with campaign and performance data in one place."
          cta={{ href: "/platform/campaign-intelligence" }}
        />
      )}
    </TabSection>
  );
}

type UseCaseTabsProps = {
  /**
   * Shows an extended version of use cases, with four columns of copy instead of three.
   * @default false
   */
  isExtended?: boolean;

  /**
   * Will show the title and tab selectors centered on their own lines.
   * @default false
   */
  isCentered?: boolean;

  /**
   * Determines if a horizontal rule should be shown above the component.
   * @default false
   */
  hasRule?: boolean;

  /**
   * A title that is shown inline with the filter buttons by default, or centered above the
   * filter buttons if a description is included.
   */
  title?: string;

  /**
   * An optional description. Will always use a centered title shown above the filter buttons.
   */
  description?: string;
};

export function UseCaseTabs({
  isExtended,
  isCentered,
  hasRule,
  title = "Solve real problems, fast",
  description,
}: UseCaseTabsProps) {
  const useSeparateTitle = Boolean(isCentered ?? description);

  return (
    <Section mt={description ? sectionSpacingY : { base: "huge", md: "huger" }}>
      {hasRule && <Rule />}

      {useSeparateTitle && (
        <TextTitle size="lg" align="center" title={title} description={description} descriptionMaxW="620px" />
      )}

      <FilterTabs
        mt="bigger"
        leftJustifiedTitle={useSeparateTitle ? "" : title}
        tabs={[
          {
            label: "Lifecycle marketing",
            items: <FirstTabSection isExtended={isExtended} />,
          },
          {
            label: "Paid marketing",
            items: <SecondTabSection isExtended={isExtended} />,
          },
          {
            label: "Experimentation",
            items: <ThirdTabSection isExtended={isExtended} />,
          },
        ]}
      />
    </Section>
  );
}
